import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { hotTopics } from "@/mocks/site";
import { events } from "@/mocks/events";

const heroImg =
  "https://readdy.ai/api/search-image?query=warm%20editorial%20flat%20illustration%20cream%20vermilion%20and%20amber%20palette%2C%20large%20top-down%20view%20of%20a%20research%20board%20where%20threads%20connect%20pinned%20notes%20and%20timeline%20markers%20forming%20a%20clear%20map%2C%20tiny%20watermelon%20slice%20and%20magnifier%20accents%2C%20soft%20paper%20grain%2C%20minimal%20magazine%20infographic%20style%2C%20generous%20negative%20space%2C%20high%20detail&width=1040&height=800&seq=home-hero-board-01&orientation=landscape";

export default function Hero() {
  const navigate = useNavigate();
  const [kw, setKw] = useState("");
  const [focus, setFocus] = useState(false);
  const top = events.find((e) => e.slug === hotTopics[0].slug) || events[0];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = kw.trim();
    navigate(q ? `/events?q=${encodeURIComponent(q)}` : "/events");
  };

  return (
    <section className="relative overflow-hidden bg-background-100">
      {/* 装饰底 */}
      <div className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full bg-accent-200/50 blur-3xl"></div>
      <div className="pointer-events-none absolute -right-16 bottom-0 h-80 w-80 rounded-full bg-primary-200/50 blur-3xl"></div>

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-2 lg:gap-12">
        {/* 文案 */}
        <div>
          <p className="inline-flex items-center gap-2 rounded-full border border-accent-300 bg-accent-50 px-3 py-1 text-xs font-medium text-accent-800">
            <span className="flex h-2 w-2 items-center justify-center">
              <span className="h-2 w-2 animate-pulse rounded-full bg-accent-500"></span>
            </span>
            线索串联 · 时间线 · 观点光谱 · 信息溯源
          </p>

          <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
            再复杂的瓜，
            <br className="hidden md:block" />
            也能<span className="text-primary-600">一条路线吃透</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-foreground-700 md:text-lg">
            吃瓜路线不是催更八卦的账号，而是一张「事件地图」。我们把散落在社交平台、媒体与公告里的零散线索，用时间线、关键人物、各方观点与溯源清单重新串起来——帮你不再云里雾里，也不被带偏。
          </p>

          {/* 搜索 */}
          <form
            onSubmit={submit}
            className={`mt-7 flex max-w-xl items-center gap-2 rounded-lg border bg-background-50 p-1.5 transition-all ${
              focus ? "border-primary-300 ring-2 ring-primary-100" : "border-background-300"
            }`}
          >
            <span className="pl-3 text-foreground-400">
              <i className="ri-search-line text-lg" aria-hidden="true"></i>
            </span>
            <input
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              onFocus={() => setFocus(true)}
              onBlur={() => setFocus(false)}
              type="search"
              placeholder="想理清哪件事？输入关键词检索事件地图…"
              aria-label="检索事件地图"
              className="w-full bg-transparent py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              去理清
              <i className="ri-arrow-right-line text-sm" aria-hidden="true"></i>
            </button>
          </form>

          {/* 快捷标签 */}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">大家都在找：</span>
            {events.slice(0, 4).map((e) => (
              <Link
                key={e.slug}
                to={`/events/${e.slug}`}
                className="rounded-full border border-background-200 bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                {e.title.slice(0, 8)}…
              </Link>
            ))}
          </div>

          <dl className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-background-200 pt-5">
            {[
              { n: "32", t: "份事件地图" },
              { n: "4", t: "大内容栏目" },
              { n: "10+", t: "栏目来源标注" },
            ].map((s) => (
              <div key={s.t}>
                <dt className="sr-only">{s.t}</dt>
                <dd className="font-heading text-xl font-bold text-primary-700 md:text-2xl">{s.n}</dd>
                <dd className="mt-0.5 text-xs text-foreground-500">{s.t}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* 插画 */}
        <div className="relative mx-auto w-full max-w-md lg:max-w-none">
          <div className="relative overflow-hidden rounded-xl border border-background-200 bg-background-50 p-2 shadow-none">
            <img
              src={heroImg}
              alt="用线条连接零散线索的事件地图示意图"
              className="aspect-[4/3] w-full rounded-lg object-cover object-center"
            />
          </div>

          {/* 浮层热榜卡 */}
          <Link
            to={`/events/${top.slug}`}
            className="absolute -bottom-4 -left-2 flex max-w-[15rem] items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-3 md:-left-6"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
              <i className="ri-fire-fill" aria-hidden="true"></i>
            </span>
            <span>
              <span className="block text-[10px] uppercase tracking-widest text-accent-700">
                热点坐标 · 第 1 位
              </span>
              <span className="mt-1 block text-sm font-semibold leading-snug text-foreground-900 line-clamp-2">
                {top.title}
              </span>
            </span>
          </Link>

          <span className="absolute -right-2 top-6 inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 md:-right-3">
            <i className="ri-refresh-line" aria-hidden="true"></i>
            持续更新中
          </span>
        </div>
      </div>
    </section>
  );
}